/** Blank styles shown in the dropdown before the "see all" link. */
const catalogSuggestionLimit = 3;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();

    // Shopify's suggestions cover the ready-made designs only. The blanks are
    // a second index on the catalog app, fetched separately and appended once
    // it answers, so the design results are never held back waiting for it.
    this.renderCatalogGroup();
  }

  /**
   * Append matching blank styles from the catalog app under the storefront's
   * own suggestions.
   *
   * Written into the DOM rather than into `cachedResults` so a slow or failed
   * catalog never becomes a cached-empty result for that term, and so the
   * design results paint at their normal speed.
   */
  renderCatalogGroup() {
    if (!window.CatalogSearch) return;

    const searchTerm = this.searchTerm;
    const resultsContainer = this.querySelector('#predictive-search-results');
    if (!searchTerm || !resultsContainer) return;

    window.CatalogSearch.searchCatalog(searchTerm, catalogSuggestionLimit).then((results) => {
      // Between the request and its answer the shopper may have typed on, or
      // closed the dropdown, and the container will have been replaced.
      if (this.searchTerm !== searchTerm || !resultsContainer.isConnected) return;
      if (results.total === 0) return;

      const group = this.buildCatalogGroup(searchTerm, results);
      // The catalog group belongs above the "search for …" button that closes
      // the dropdown; appending is only the fallback if Dawn ever drops it.
      const searchForButton = resultsContainer.querySelector('#predictive-search-option-search-keywords');
      if (searchForButton) {
        searchForButton.before(group);
      } else {
        resultsContainer.appendChild(group);
      }
    });
  }

  buildCatalogGroup(searchTerm, results) {
    const { catalogProductUrl, catalogSearchUrl, formatPrice } = window.CatalogSearch;

    const group = document.createElement('div');
    group.className = 'predictive-search__result-group';

    const heading = document.createElement('h2');
    heading.id = 'predictive-search-catalog';
    heading.className =
      'predictive-search__heading predictive-search__heading--catalog text-body caption-with-letter-spacing';
    heading.appendChild(document.createTextNode('Blanks catalog'));

    const meta = document.createElement('span');
    meta.className = 'predictive-search__catalog-meta';
    meta.textContent = `${results.total.toLocaleString()} styles`;
    heading.appendChild(meta);
    group.appendChild(heading);

    const list = document.createElement('ul');
    list.id = 'predictive-search-results-catalog-list';
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-labelledby', 'predictive-search-catalog');

    results.products.slice(0, catalogSuggestionLimit).forEach((product, index) => {
      list.appendChild(this.buildCatalogItem(product, index, catalogProductUrl, formatPrice));
    });

    // A dropdown can only ever show a handful; this is the way to the rest.
    list.appendChild(this.buildCatalogAllItem(searchTerm, results, catalogSearchUrl));

    group.appendChild(list);
    return group;
  }

  buildCatalogItem(product, index, catalogProductUrl, formatPrice) {
    const item = document.createElement('li');
    // Dawn's arrow-key handling walks every `li` in the component and points
    // `aria-activedescendant` at the selected one, so these need the same
    // option role and a unique id.
    item.id = `predictive-search-option-catalog-${index + 1}`;
    item.className = 'predictive-search__list-item';
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', 'false');

    const link = document.createElement('a');
    link.className = 'predictive-search__item link link--text';
    link.href = catalogProductUrl(product);
    link.tabIndex = -1;

    if (product.image) {
      const image = document.createElement('img');
      image.className = 'predictive-search__image';
      image.src = product.image;
      image.alt = product.name;
      image.width = 50;
      image.height = 50;
      image.loading = 'lazy';
      link.appendChild(image);
    }

    const content = document.createElement('div');
    content.className = 'predictive-search__item-content';

    const brand = document.createElement('div');
    brand.className = 'predictive-search__item-vendor caption-with-letter-spacing';
    brand.textContent = product.brand || product.category || '';
    content.appendChild(brand);

    const title = document.createElement('p');
    title.className = 'predictive-search__item-heading h5';
    title.textContent = product.name;
    content.appendChild(title);

    const price = formatPrice(product.price);
    if (price) {
      const priceEl = document.createElement('div');
      priceEl.className = 'price';
      priceEl.textContent = `from ${price}`;
      content.appendChild(priceEl);
    }

    link.appendChild(content);
    item.appendChild(link);
    return item;
  }

  buildCatalogAllItem(searchTerm, results, catalogSearchUrl) {
    const item = document.createElement('li');
    item.id = 'predictive-search-option-catalog-all';
    item.className = 'predictive-search__list-item';
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', 'false');

    const link = document.createElement('a');
    link.className = 'predictive-search__item link link--text';
    link.href = catalogSearchUrl(searchTerm);
    link.tabIndex = -1;

    const content = document.createElement('div');
    content.className = 'predictive-search__item-content predictive-search__item-content--centered';
    const heading = document.createElement('p');
    heading.className = 'predictive-search__item-heading h5';
    heading.textContent = `See all ${results.total.toLocaleString()} blanks for "${searchTerm}"`;
    content.appendChild(heading);

    link.appendChild(content);
    item.appendChild(link);
    return item;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
